import { put, call } from 'redux-saga/effects'
import * as types from '../actions/actionTypes'
import {
  setItemUnavailable,
  setSlashActiveAction,
} from '../actions/landingActions'
import {
  getPaymentInfoGuestAction,
  getPaymentInfoMemberAction,
} from '../actions/paymentInfoActions'
import { setSnackbarErrorAction } from '../actions/snackbarActions'
import { checkoutTapPayErrorAction } from '../actions/tapPayActions'
import * as api from '../api'
import { ga4Event } from '../components/libs/ga4'
import { Errno, getErrorMessage } from '../domain/Errno'
import { TAPPAY } from '../libs/constants'
import loadScript from '../libs/loadScript'
import * as tapPayHelpers from '../libs/tapPay'

export function* checkoutTapPaySaga({ payload }) {
  try {
    yield put({ type: types.FETCHING })
    const primeResult = payload.skipTapPay
      ? { prime: '0' }
      : yield call(tapPayHelpers.getPrime)

    const res = yield call(api.requestCreateTapPayOrder, {
      ...payload,
      ...primeResult,
      skipTapPay: undefined,
      maxQuantity: undefined,
    })

    yield put({
      type: types.CHECKOUT_TAPPAY_SUCCESS,
      payload: { ...res.data, qty: payload.qty, max_qty: payload.maxQuantity },
    })
  } catch (err) {
    console.error('checkoutTapPaySaga', err)
    if (!err.data) {
      yield put(setSnackbarErrorAction(getErrorMessage(err)))
    } else {
      const { errno, message } = err.data
      if (message === '"contact_email" must be a valid email') {
        yield put(setSnackbarErrorAction('請輸入完整有效的電子信箱'))
      } else {
        let stayAtPurchase = false
        switch (errno) {
          case Errno.ERR_AIFIAN_PAYMENT_SERVICE_SLASH_INACTIVE:
            yield put(setSlashActiveAction(false))
            break
          case Errno.ERR_SLASH_INSUFFICIENT_DISCOUNT_TOKEN:
            yield put(setSnackbarErrorAction('回饋不足支付僅限折抵之商品')) // TODO
            yield put(setItemUnavailable()) // * back to item intro
            break
          // case Errno.ERR_SLASH_INSUFFICIENT_QTY:
          //   yield put(setSnackbarErrorAction('商品已售完'))
          //   yield put(setItemUnavailable()) // * back to item intro
          //   break
          case Errno.ERR_SLASH_INSUFFICIENT_QTY:
          case Errno.ERR_SLASH_PURCHASE_QTY_EXCEEDED: {
            // * SWA-330: reload item info and purchase info
            const itemId = payload.cream_item_id
            yield put({
              type: types.GET_ITEM_ENTRY_INFO,
              payload: itemId,
            })
            yield put(
              payload.isGuest
                ? getPaymentInfoGuestAction(itemId)
                : getPaymentInfoMemberAction(itemId),
            )
            yield put(setSnackbarErrorAction('商品數量不足，請重新選擇數量'))
            ga4Event(
              errno === Errno.ERR_SLASH_INSUFFICIENT_QTY
                ? 'slash_preview_error_message_insufficient'
                : 'slash_preview_error_message_exceed',
            )
            stayAtPurchase = true
            break
          }
          case Errno.ERR_SLASH_ITEM_CLOSED:
            yield put(setSnackbarErrorAction('已不供應此商品'))
            ga4Event('slash_preview_error_message_closed')
            yield put({ type: types.TERMINATE_ITEM })
            yield put(setItemUnavailable()) // * back to item intro
            break
          case Errno.ERR_SLASH_ITEM_EXPIRED:
            yield put(setSnackbarErrorAction('購買時間已截止'))
            ga4Event('slash_preview_error_message_expired')
            yield put(setItemUnavailable()) // * back to item intro
            break
        }
        yield put(
          checkoutTapPayErrorAction(
            tapPayHelpers.isTapPayErrorFailure(err.status, errno),
            errno,
            stayAtPurchase,
          ),
        )
      }
    }
  } finally {
    yield put({ type: types.FETCH_COMPLETE })
  }
}

export function* loadTapPaySDKSaga({ payload }) {
  try {
    yield call(loadScript, payload.url, payload.id)

    if (tapPayHelpers.getSDK().appID >= 0) {
      // * already loaded
      return
    }

    const sdkConfig = { server_type: TAPPAY.ENV }
    try {
      const { memberQuery, guest } = payload
      const { data: sdkConfigData } = yield guest
        ? call(api.requestGetTapPayConfigGuest, guest)
        : call(api.requestGetTapPayConfigMember, memberQuery)
      Object.assign(sdkConfig, sdkConfigData)
    } catch (ignored) {
      // continue
    }

    if (!sdkConfig.app_id || !sdkConfig.app_key) {
      throw new Error('missing setupSDK args')
    }

    yield call(tapPayHelpers.setupSDK, sdkConfig)
    yield put({ type: types.LOAD_TAPPAY_SDK_SUCCESS })
  } catch (err) {
    console.error('loadTapPaySDKSaga', err)
    yield put({ type: types.LOAD_TAPPAY_SDK_ERROR })
  }
}

/*
 payload: { member_id, period_id }
 */
export function* checkoutTapPayInstalmentSaga({ payload }) {
  try {
    yield put({ type: types.FETCHING })
    const primeResult = yield call(tapPayHelpers.getPrime)

    const res = yield call(api.requestCreateTapPayInstalment, {
      ...payload,
      prime: primeResult.prime,
    })

    yield put({
      type: types.CHECKOUT_TAPPAY_INSTALMENT_SUCCESS,
      payload: res.data,
    })
  } catch (err) {
    console.error('checkoutTapPayInstalmentSaga', err)
    if (!err.data) {
      yield put(setSnackbarErrorAction(getErrorMessage(err)))
    } else {
      const { errno } = err.data
      yield put({
        type: types.CHECKOUT_TAPPAY_INSTALMENT_ERROR,
        payload: {
          errno,
          failed: tapPayHelpers.isTapPayErrorFailure(err.status, errno),
        },
      })
      if (errno === Errno.ERR_AIFIAN_PAYMENT_SERVICE_SLASH_INACTIVE) {
        yield put(setSlashActiveAction(false))
      }
    }
  } finally {
    yield put({ type: types.FETCH_COMPLETE })
  }
}

export function* checkoutTapPayPeriodicSaga({ payload }) {
  try {
    yield put({ type: types.FETCHING })
    const primeResult = yield call(tapPayHelpers.getPrime)

    const res = yield call(api.requestCreateTapPayPeriodic, {
      ...payload,
      prime: primeResult.prime,
    })

    yield put({
      type: types.CHECKOUT_TAPPAY_PERIODIC_SUCCESS,
      payload: res.data,
    })
  } catch (err) {
    console.error('checkoutTapPayPeriodicSaga', err)
    if (!err.data) {
      yield put(setSnackbarErrorAction(getErrorMessage(err)))
    } else {
      const { errno } = err.data
      yield put({
        type: types.CHECKOUT_TAPPAY_PERIODIC_ERROR,
        payload: {
          errno,
          failed: tapPayHelpers.isTapPayErrorFailure(err.status, errno),
        },
      })
      if (errno === Errno.ERR_AIFIAN_PAYMENT_SERVICE_SLASH_INACTIVE) {
        yield put(setSlashActiveAction(false))
      }
    }
  } finally {
    yield put({ type: types.FETCH_COMPLETE })
  }
}
